/**
 * Button component with consistent design system patterns.
 *
 * Uses class-variance-authority (CVA) for type-safe variant handling.
 * Supports multiple variants, sizes, loading states, and icons.
 */

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Spinner } from '@/design-system/atoms/Spinner'

/**
 * Button variant definitions using CVA.
 *
 * Base styles apply to all buttons, variants define specific color schemes,
 * sizes control padding and typography, fullWidth makes button 100% width.
 */
const buttonVariants = cva(
  // Base styles - common to all buttons
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary: MDi red brand - ONLY for main actions
        primary:
          'bg-primary-600 text-white shadow-theme-xs hover:bg-primary-700 hover:shadow-md focus-visible:ring-primary-500 dark:bg-primary-600 dark:hover:bg-primary-700',
        // Secondary: Neutral and elegant - general use
        secondary:
          'bg-surface-elevated text-content-primary border border-gray-300 hover:bg-surface-tertiary hover:border-gray-400 hover:shadow-sm focus-visible:ring-gray-400 dark:border-gray-700 dark:hover:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700',
        // Outline: Subtle and discreet
        outline:
          'bg-transparent text-content-primary ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-gray-400 dark:ring-gray-700 dark:hover:bg-white/5',
        // Ghost: Minimal styling, no border
        ghost:
          'bg-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus-visible:ring-gray-400 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800',
        // Danger: Red for destructive actions
        danger:
          'bg-red-600 text-white shadow-theme-xs hover:bg-red-700 hover:shadow-md focus-visible:ring-red-500',
        // Success: Green for positive actions
        success:
          'bg-green-600 text-white shadow-theme-xs hover:bg-green-700 hover:shadow-md focus-visible:ring-green-500',
        // Link: Appears as a link
        link: 'text-primary-600 underline-offset-4 hover:underline dark:text-primary-400',
      },
      size: {
        xs: 'h-7 px-2 text-xs',
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-base',
        lg: 'h-12 px-6 text-lg',
        xl: 'h-14 px-8 text-xl',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
)

/**
 * Button component props.
 *
 * Extends standard HTML button attributes and adds custom props.
 */
export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Shows loading spinner and disables button */
  loading?: boolean
  /** Icon to display before button text */
  leftIcon?: React.ReactNode
  /** Icon to display after button text */
  rightIcon?: React.ReactNode
  /** Also known as startIcon for compatibility */
  startIcon?: React.ReactNode
  /** Also known as endIcon for compatibility */
  endIcon?: React.ReactNode
}

/**
 * Button component with design system patterns.
 *
 * Features:
 * - Consistent variants using CVA
 * - Loading states with spinner
 * - Icon support (left/right)
 * - Full accessibility support
 * - Dark mode support
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md">
 *   Save Changes
 * </Button>
 *
 * <Button variant="outline" loading>
 *   Processing...
 * </Button>
 *
 * <Button variant="danger" leftIcon={<TrashIcon />}>
 *   Delete
 * </Button>
 * ```
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading,
      leftIcon,
      rightIcon,
      startIcon,
      endIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    // Support both leftIcon/rightIcon and startIcon/endIcon for compatibility
    const iconLeft = leftIcon || startIcon
    const iconRight = rightIcon || endIcon

    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <Spinner className="h-4 w-4" size="sm" />
        ) : iconLeft ? (
          <span className="flex items-center">{iconLeft}</span>
        ) : null}
        {children}
        {iconRight && !loading && (
          <span className="flex items-center">{iconRight}</span>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

// Default export for convenience
export default Button

// Export variants for reuse in other components
export { buttonVariants }
